import { LabelMode } from "./../../../../../../utils/label-mode.enum";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "src/app/pages/auth";
import { environment } from "src/environments/environment";
import { SharedModule } from "src/shared/shared.module";
import { AddressFormComponent } from "./components/address-form/address-form.component";
import { AddressService } from "src/app/services/api/address.service";
import { lastValueFrom } from "rxjs";
import { Address } from "src/app/services/model/address.model";
import { SweetAlertService } from "src/app/services/sweet-alert-service/sweet-alert.service";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { SetError } from "src/utils/set-error.validator";
import { UserService } from "src/app/services/api/user.service";
import { Role } from "src/utils/enum/role.enum";

@Component({
  selector: "app-user-profile",
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AddressFormComponent,
  ],
  templateUrl: "./user-profile.component.html",
  styleUrl: "./user-profile.component.scss",
})
export class UserProfileComponent implements OnInit {
  #fb = inject(FormBuilder);
  #authService = inject(AuthService);
  #userService = inject(UserService);
  #addressService = inject(AddressService);
  #swalService = inject(SweetAlertService);
  #toastService = inject(ToastService);

  currentUser = this.#authService.currentUser()!.user;
  userAddress = this.#authService.userAddress;
  fileUrl = environment.apiFileUrl;
  profileImg = this.fileUrl + this.currentUser.data.image_url;
  userType = this.currentUser.role.id === 2 ? Role.MEMBER_EN : Role.PARENT_EN;
  addressFormActive = signal<boolean>(false);
  selectedAddress = signal<Address | null>(null);

  form: FormGroup;
  LabelMode = LabelMode;
  error: any;

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm() {
    this.form = this.#fb.group({
      id: [null],
      image: [null],
      image_url: [null],
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, Validators.required],
      birthdate: [null, Validators.required],
      academy: [null],
      nickname: [null],
    });
    this.form.patchValue(this.currentUser.data);
  }

  selectImage(): void {
    document.getElementById("image-input")?.click();
  }

  async updateProfile() {
    this.error = {};
    if (this.form.invalid) {
      this.error = SetError(this.form);
      // Say something. `SetError` marks the fields, but on an account that is
      // missing a required value the button simply did nothing and gave no
      // reason — which reads as a broken save, not as a validation failure.
      this.#toastService.toastWarning("กรุณากรอกข้อมูลที่จำเป็นให้ครบก่อนบันทึก");
      return;
    }
    let payload = this.form.getRawValue();
    await this.#swalService.swalConfirmation();
    try {
      const updateProfile = await lastValueFrom(
        this.#userService.updateUser(payload.id, payload, this.userType!)
      );
      this.#toastService.toastSuccess("บันทึกข้อมูลสำเร็จ");
      await lastValueFrom(this.#authService.getUserByToken());
    } catch (error: any) {
      this.#toastService.toastError("เกิดข้อผิดพลาด: " + error.error.errors);
    }
  }

  editAddress(address: Address) {
    this.selectedAddress.update(() => address);
    this.toggleAddressForm(true);
  }
  async deleteAddress(address: Address) {
    await this.#swalService.swalConfirmation();
    let address_id = address.id;
    try {
      const deleteAddress = await lastValueFrom(
        this.#addressService.deleteAddress(address_id)
      );
      this.#toastService.toastSuccess("ลบข้อมูลสำเร็จ");
      this.fetchAddress();
    } catch (error: any) {
      this.#toastService.toastError("เกิดข้อผิดพลาด: " + error.error.errors);
    }
  }

  async toggleAddressForm(toggleState: boolean) {
    this.addressFormActive.update(() => toggleState);
    if (!toggleState) {
      this.fetchAddress();
    }
  }

  async fetchAddress() {
    const updateAddress = await lastValueFrom(
      this.#addressService.getAddresses()
    );
    this.#authService.userAddress.update(() => updateAddress);
    this.userAddress.update(() => updateAddress);
  }
}
